import React, { useContext, useEffect } from "react";
import { Dropdown, InfoMessage, Shimmer } from "sprint-common-web";

import { CloudVideoAssetItem } from "../../../../../../common/api/master-api/assets/get-cloud-video-assets/GetCloudVideoAssetsResponse";
import { getLanguageDisplayName } from "../../../../../../common/types/Language";
import { AuthContext } from "../../../../../../core/security/AuthProvider";
import { useCloudFileDashboard } from "../../../common/context/useCloudFileDashboard";
import { useCloudFileDetails } from "../../../common/useCloudFileDetails";
import { AudioPreview } from "./audio/AudioPreview";
import { useCloudFilePreviewContext } from "./hooks/useCloudFilePreviewContext";
import { SubtitlePreview } from "./subtitle/SubtitlePreview";
import { FileType } from "./types/CloudFilePreviewItem";
import { VideoPreview } from "./video/VideoPreview";

export const CloudFilePreview = (): JSX.Element | null => {
    const {
        filePreview,
        isPreviewLoading,
        isZip,
        isPreviewAvailable,
        reloadWithConformingVideo,
        reloadPreview,
        availableVideoFiles,
        selectedVideoCloudFileId
    } = useCloudFilePreviewContext();
    const { data: cloudFileDetails } = useCloudFileDetails();
    const { sprintUser } = useContext(AuthContext);
    const { selectedVersionInfo } = useCloudFileDashboard();

    const versionIds = getVersionIds(selectedVersionInfo?.versionIds);

    const shouldReloadPrev =
        isPreviewAvailable && (!filePreview || reloadWithConformingVideo) && cloudFileDetails?.cloudFileId;

    useEffect(() => {
        if (!shouldReloadPrev) return;

        reloadPreview({
            cloudFileId: cloudFileDetails.cloudFileId,
            versionIds
        });
    }, [cloudFileDetails?.cloudFileId, selectedVersionInfo?.versionIds]);

    if (!cloudFileDetails || !filePreview) {
        return <Shimmer />;
    }

    if (!isPreviewAvailable) {
        return <InfoMessage message="Preview for selected file type is not available" />;
    }

    const cloudFileId = cloudFileDetails.cloudFileId;

    const handleVideoFileChange = (videoCloudFileId: string) => {
        reloadPreview({
            cloudFileId,
            versionIds,
            videoCloudFileId
        });
    };

    switch (filePreview?.type) {
        case FileType.Audio: {
            return <AudioPreview url={filePreview.url} accessToken={sprintUser.accessToken} />;
        }

        case FileType.Subtitle: {
            return <SubtitlePreview url={filePreview.url} isZip={isZip} accessToken={sprintUser.accessToken} />;
        }

        case FileType.Video: {
            return (
                <VideoPreviewWithSelector
                    availableVideoFiles={availableVideoFiles}
                    selectedVideoCloudFileId={selectedVideoCloudFileId}
                    isPreviewLoading={isPreviewLoading}
                    cloudFileId={cloudFileId}
                    manifestUrl={filePreview.url}
                    onVideoFileChange={handleVideoFileChange}
                />
            );
        }

        default: {
            return <></>;
        }
    }
};

const getVersionIds = (selectedVersionIds?: string[]) => {
    return selectedVersionIds?.length ? selectedVersionIds : undefined;
};

interface VideoPreviewWithSelectorProps {
    availableVideoFiles: CloudVideoAssetItem[];
    selectedVideoCloudFileId: string | undefined;
    isPreviewLoading: boolean;
    cloudFileId: string;
    manifestUrl: string;
    onVideoFileChange: (videoCloudFileId: string) => void;
}

const VideoPreviewWithSelector = ({
    availableVideoFiles,
    selectedVideoCloudFileId,
    isPreviewLoading,
    cloudFileId,
    manifestUrl,
    onVideoFileChange
}: VideoPreviewWithSelectorProps): JSX.Element => (
    <>
        {availableVideoFiles.length > 0 && (
            <Dropdown
                selectedKey={selectedVideoCloudFileId}
                options={availableVideoFiles.map((v) => mapCloudVideoAssetToDropdownOption(v))}
                onChange={onVideoFileChange}
                styles={{ root: { paddingBottom: 30 } }}
            />
        )}
        {isPreviewLoading ? (
            <Shimmer />
        ) : (
            <VideoPreview cloudFileId={cloudFileId} manifestUrl={manifestUrl} width={"50vw"} />
        )}
    </>
);

const mapCloudVideoAssetToDropdownOption = (asset: CloudVideoAssetItem) => ({
    key: asset.cloudFileId,
    text: `${asset.filePath} | ${asset.aspectRatio}, ${asset.resolution.width}x${
        asset.resolution.height
    }, ${getLanguageDisplayName(asset.embeddedSubtitles)}`
});
